import React, {useEffect} from 'react';
import {SliceStatus, UnsplashImage, WellniteContentArticle} from 'interfaces';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {getWellniteArticlesByProvider} from '../WellniteContent/wellniteContentActions';
import {
  selectWellniteArticles,
  selectWellniteContentStatus,
} from '../WellniteContent/wellniteContentSelectors';

interface ArticleListProps {
  providerId: string;
}

export function ProviderArticleList({
  providerId,
}: ArticleListProps): React.ReactElement {
  const dispatch = useDispatch();
  const articles = useSelector(selectWellniteArticles);
  const isPending =
    useSelector(selectWellniteContentStatus) === SliceStatus.pending;
  const {t} = useTranslation();

  useEffect(() => {
    if (!isPending && providerId) {
      dispatch(getWellniteArticlesByProvider({providerId}));
    }
  }, [providerId]);

  return (
    <section className="text-left @container">
      <h2 className="text-md pt-4 pb-4 font-semibold">
        {t('wellniteContent.directBookingArticlesTitle', 'Articles')}
      </h2>

      {!isPending && providerId ? (
        <div className="grid grid-cols-1 gap-10">
          {articles?.map(article => (
            <Article key={article._id} article={article} />
          ))}
        </div>
      ) : (
        <ArticlesSkeletonLoader />
      )}
    </section>
  );
}

interface ArticleProps {
  article: WellniteContentArticle;
}

export function Article({article}: ArticleProps): React.ReactElement {
  const image = article.content?.find(c => c.type === 'image')
    ?.value as UnsplashImage;

  return (
    <article>
      <a
        href={`/post/${article.slug}`}
        className="flex items-center space-x-5 text-blue-600 underline hover:text-blue-500"
      >
        <span className="inline-block w-20 h-16 flex-shrink-0">
          <img
            className="w-full h-full object-cover rounded-xl"
            src={image.urls.thumb}
            alt={image.alt_description ?? article.title}
          />
        </span>
        <span className="inline-block">{article.title}</span>
      </a>
    </article>
  );
}

const ArticlesSkeletonLoader = () => {
  return (
    <div className=" space-y-4 gap-4">
      {[...Array(5)].map((_, index) => (
        <div key={index} className="flex items-center space-x-4">
          <div className="w-20 h-16 bg-gray-300 rounded-xl animate-pulse"></div>
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-gray-300 rounded animate-pulse w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded animate-pulse w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  );
};
